/**
 * Pure helpers for {@link WorkshopPicker} (BON-142, generalized in BON-146):
 * the calendar's presentational `CalendarEvent`, filter-option derivation, and
 * month/day grouping for the list and day drawer.
 *
 * These all speak {@link PickerWorkshop}, NOT an API type. The picker serves two
 * data sources now — the workshops list (create a booking) and the booking's
 * rebook targets (move a booking) — whose envelopes differ, so both are
 * normalized at the boundary by `pickerWorkshop.ts` and everything below this
 * line is source-agnostic. Split out so `WorkshopPicker.tsx` stays under the
 * repo's line cap.
 */
import { format } from 'date-fns';

import type { RegionNode } from '../../../api/regions';
import type { Workshop } from '../../../api/workshops';
import { formatLocalTime, type CalendarEvent } from '../../../shared/workshopCalendarPicker';
import { formatVenueDate } from '../../../utils/venueTime';

import type { PickedWorkshop } from './CreateBookingModalHelpers';
import type { PickerWorkshop } from './pickerWorkshop';

/** Only bookable workshops belong in the create-booking picker — the backend
 *  rejects a non-`active` lifecycle outright (`assertWorkshopBookable`), so
 *  filter them out at FETCH time rather than showing a pick that will always
 *  404. Typed on the raw `Workshop` because that is where it runs: the rebook
 *  listing has no equivalent, since the server already applies the rule and
 *  returns anything it excluded as an unselectable row with a reason. */
export function isPickableWorkshop(w: Workshop): boolean {
  return w.lifecycle === 'active';
}

export interface PickerAvailability {
  /** True when the row/pill must be unselectable. */
  disabled: boolean;
  /** Why — the short label a row shows in place of the seat count (the calendar
   *  pill instead surfaces this via `disabledReason`). Null when selectable. */
  reason: string | null;
}

/** Single source of truth for whether a picker row is selectable and why — so
 *  the calendar pill, list row and day-drawer row all agree. The verdict is no
 *  longer computed here: it is decided once per row at the mapping boundary
 *  (the server's answer for a rebook target, ended/sold-out for a workshop),
 *  which is what lets one picker serve both. */
export function pickerAvailability(w: PickerWorkshop): PickerAvailability {
  return { disabled: !w.selectable, reason: w.selectable ? null : w.reasonLabel };
}

/** Seats text for a LIST/DRAWER row: the disabled reason when disabled, else
 *  "N left". The calendar pill uses the raw "N left" instead (with the reason
 *  carried in `disabledReason`). */
export function rowSeatsLabel(w: PickerWorkshop): string {
  return pickerAvailability(w).reason ?? `${w.seatsLeft} left`;
}

export function toPickedWorkshop(w: PickerWorkshop): PickedWorkshop {
  return {
    id: w.id,
    typeName: w.typeName,
    treeSpeciesName: w.treeSpeciesName,
    venueName: w.venueName,
    venueCity: w.venueCity,
    venueState: w.venueState,
    // Venue-local wall clock (naive, no `Z`) — this field is display-only in the
    // booking modal, so it carries the wall clock rather than the absolute
    // instant (BON-386). See PickedWorkshop's doc.
    startsAt: w.localStartsAt ?? w.startsAt,
    priceCents: w.priceCents,
    seatsLeft: w.seatsLeft,
  };
}

/** "Wed May 27" from a venue-local wall clock (naive, no `Z`) — no zone
 *  re-anchoring (BON-386). */
export function formatPickerDate(local: string | null): string {
  return formatVenueDate(local, 'EEE MMM d', '');
}

/** "6:30p" — reads the wall-clock hour/minute straight off the venue-local ISO
 *  string without timezone re-anchoring (via `calendarEvent.ts#formatLocalTime`). */
export function formatPickerTime(local: string | null): string {
  return local ? formatLocalTime(local) : '';
}

/** "2026-05" grouping key for the mobile list, keyed on the venue-local
 *  wall-clock month (BON-386). */
export function monthKey(local: string | null): string {
  return local ? local.slice(0, 7) : '';
}

/** "MAY 2026" label for the mobile list, from the venue-local wall clock. */
export function monthLabel(local: string | null): string {
  return formatVenueDate(local, 'MMM yyyy', '').toUpperCase();
}

/** Cents → "$65.00/seat". */
export function formatPricePerSeat(cents: number): string {
  const amount = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    cents / 100,
  );
  return `${amount}/seat`;
}

export interface FilterOption {
  id: string;
  label: string;
}

/** Distinct tree-species options present in the listed workshops, sorted by
 *  name — derived from the dataset itself rather than a separate endpoint. */
export function deriveTreeOptions(workshops: PickerWorkshop[]): FilterOption[] {
  const byId = new Map<string, string>();
  for (const w of workshops) {
    if (w.treeSpeciesId && w.treeSpeciesName) byId.set(w.treeSpeciesId, w.treeSpeciesName);
  }
  return Array.from(byId, ([id, label]) => ({ id, label })).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

/** Map a picker workshop onto the shared calendar's presentational event shape
 *  (BON-386). `startsAt` keeps the absolute instant (sort + initial-month
 *  anchor); `localStartsAt` gets the venue-local wall clock — the picker
 *  calendar buckets day cells on `localStartsAt.slice(0,10)`, so this makes both
 *  the bucketing AND the pill's clock the venue's wall clock, with no zone
 *  re-anchoring.
 *
 *  The source workshop gets a "Current" BADGE rather than having its reason
 *  swallowed by the seats line: it is the anchor a CSR judges every other date
 *  against, so it has to stay legible as a chip, not read as a failure
 *  (BON-146). */
export function toCalendarEvent(w: PickerWorkshop): CalendarEvent {
  const local = w.localStartsAt;
  const time = formatPickerTime(local);
  const { disabled, reason } = pickerAvailability(w);
  const seatsLabel = `${w.seatsLeft} left`;
  const isSource = w.reasonCode === 'IS_SOURCE';
  return {
    id: w.id,
    startsAt: w.startsAt,
    localStartsAt: local ?? w.startsAt,
    title: w.treeSpeciesName ?? w.typeName,
    subtitle: time ? `${w.venueName} · ${time}` : w.venueName,
    seatsLabel,
    disabled,
    disabledReason: reason ?? undefined,
    badge: isSource ? 'Current' : undefined,
    // Full detail for the hover/focus tooltip — the pill itself only shows
    // truncated text (BON-142). When disabled, the tooltip surfaces the reason
    // in place of the seat count.
    tooltip: {
      title: w.typeName,
      venueName: w.venueName,
      dateLabel: formatPickerDate(local),
      timeLabel: time,
      treeSpeciesName: w.treeSpeciesName,
      seatsLabel: reason ?? seatsLabel,
      priceLabel: formatPricePerSeat(w.priceCents),
    },
  };
}

export interface PickerMonthGroup {
  key: string;
  label: string;
  workshops: PickerWorkshop[];
}

/** Group workshops by month for the mobile list, preserving the (already
 *  ascending) order the list arrives in. */
export function groupByMonth(workshops: PickerWorkshop[]): PickerMonthGroup[] {
  const groups: PickerMonthGroup[] = [];
  const indexByKey = new Map<string, number>();
  for (const w of workshops) {
    const key = monthKey(w.localStartsAt);
    let idx = indexByKey.get(key);
    if (idx === undefined) {
      idx = groups.length;
      indexByKey.set(key, idx);
      groups.push({ key, label: monthLabel(w.localStartsAt), workshops: [] });
    }
    groups[idx].workshops.push(w);
  }
  return groups;
}

// ─── Dense calendar + day drawer (BON-142) ─────────────────────────────────

/** Below this many seats left, the drawer row's seats label switches to the
 *  warn color (matches the design's orange "3 left" vs. plain "7 left"). */
const LOW_SEATS_THRESHOLD = 5;

export function isLowSeats(seatsLeft: number): boolean {
  return seatsLeft > 0 && seatsLeft <= LOW_SEATS_THRESHOLD;
}

/** Workshops occurring on the same calendar day as `day`, using the IDENTICAL
 *  venue-local wall-clock-date bucketing the shared calendar's day cells use
 *  (`localStartsAt.slice(0,10)` — see `bucketByDay`), so the day drawer's list
 *  always matches what the cell showed (BON-386). */
export function workshopsOnDay(workshops: PickerWorkshop[], day: Date): PickerWorkshop[] {
  const key = format(day, 'yyyy-MM-dd');
  return workshops.filter((w) => (w.localStartsAt ?? w.startsAt).slice(0, 10) === key);
}

/** Find a region's display name anywhere in a (possibly nested) region tree. */
function findRegionName(regions: RegionNode[], id: string): string | null {
  for (const r of regions) {
    if (r.id === id) return r.name;
    const found = findRegionName(r.children, id);
    if (found) return found;
  }
  return null;
}

/** "all regions" when no region filter is active, else the picked region's
 *  name — feeds the day drawer's "N workshops · <scope> in view" subline. */
export function regionScopeLabel(regionId: string | null, regions: RegionNode[]): string {
  if (!regionId) return 'all regions';
  return findRegionName(regions, regionId) ?? 'this region';
}
